import { Injectable } from '@angular/core';
import { MissingCollaborationPackagesInfoDialogComponent } from '@app/collaboration-packages/components/missing-collaboration-packages-info-dialog/missing-collaboration-packages-info-dialog.component';
import { AllInclusiveService } from '@app/core/services/all-inclusive.service';
import { ClientService } from '@app/core/services/clients.service';
import { CloudCompanyService } from '@app/core/services/cloud-company.service';
import { OnboardingService } from '@app/core/services/onboarding.service';
import { openOnboardingInNewTab } from '@app/shared/misc/openBlappOnboarding';
import { openTranslatableDialog } from '@app/shared/misc/openDialog';
import { TranslocoService } from '@jsverse/transloco';
import { createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CoreActions } from '../actions';
import { AuthActions } from '../auth/auth.actions';
import { ClientActions } from '../clients/clients.actions';
import { ClientSelectors } from '../clients/clients.selectors';
import { BaseEffects } from '../core/base.effects';
import { FirmSelectors } from '../firm/firm.selectors';
import { ListActions } from '../list/list.actions';
import { ListSelectors } from '../list/list.selectors';
import { NavigationPages } from '../navigation/navigation.selectors';
import { ToastActions } from '../toast/toast.actions';
import { toClientTypeTransformer } from '../transformers/transformers';
import {
  ClientActionType,
  CollaborationPackageFilter,
  CollaborationPackageItem,
  CollaborationPackageParams,
  CollaborationPackageResponse,
} from '../types/collaboration-package.types';
import { CollaborationPackageActions } from './collaboration-packages.actions';
import { CollaborationPackageSelectors } from './collaboration-packages.selectors';

@Injectable()
export class CollaborationPackageEffects extends BaseEffects {
  loadClientsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.load),
      this.whenCurrentUrl(NavigationPages.collaborationPackages),
      map(({ filter: listFilter, params }) => ListActions.load({ filter: listFilter, params })),
    ),
  );

  loadCloudCompanyListItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.load),
      this.whenCurrentUrl(NavigationPages.collaborationPackages),
      switchMap(({ params }) =>
        this.cloudCompanyService.getCollaborationCloudCompanies(params).pipe(
          mergeMap(({ meta, data }) => [
            ListActions.loadSucceeded({ items: data }),
            CollaborationPackageActions.setMeta({ meta }),
          ]),
          this.takeUntilNavigationStart(),
          catchError(() => of(ListActions.loadFailed({ error: { message: '' } }))),
        ),
      ),
    ),
  );

  loadEmailForClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.loadEmailForClient),
      concatLatestFrom(({ clientId }) => this.store.select(ClientSelectors.getClientById(clientId))),
      switchMap(([{ clientId, cloudApiKey }, client]) => {
        if (client?.email) {
          return of(CollaborationPackageActions.loadEmailForClientSucceeded({ email: client.email }));
        }

        return this.cloudCompanyService.getCloudCompanyEmail({ clientId, cloudApiKey }).pipe(
          map((email) => CollaborationPackageActions.loadEmailForClientSucceeded({ email })),
          catchError((error: unknown) => of(CollaborationPackageActions.loadEmailForClientFailed({ error }))),
        );
      }),
    ),
  );

  loadClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.loadClient),
      switchMap(({ clientId }) => {
        if (!clientId) {
          return of(CoreActions.noAction());
        }

        return this.getClientById(clientId).pipe(
          map((client) => CollaborationPackageActions.loadClientSucceeded({ client })),
          catchError((error: unknown) => of(CollaborationPackageActions.loadClientFailed({ error }))),
        );
      }),
    ),
  );

  // -- ONBOARDING --

  onboardExistsingClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.onboardExistingClient),
      switchMap(({ clientId, email, loadRemoteVatIntervalInformation = false }) =>
        this.onboardingService
          .getOnboardingKey(
            clientId,
            { client: { email }, financialInformation: null },
            loadRemoteVatIntervalInformation,
          )
          .pipe(
            map((key) => CollaborationPackageActions.onboardExistingClientSucceeded({ key })),
            catchError((error: unknown) => of(CollaborationPackageActions.onboardExistingClientFailed({ error }))),
          ),
      ),
    ),
  );

  onboardNonExistsingClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.onboardNonExistingClient),
      switchMap(({ cloudApiKey, email, loadRemoteVatIntervalInformation = false }) =>
        this.onboardingService
          .getOnboardingKeyForNonExistingClient({ cloudApiKey, email }, loadRemoteVatIntervalInformation)
          .pipe(
            map((key) => CollaborationPackageActions.onboardNonExistingClientSucceeded({ key })),
            catchError((error: unknown) => of(CollaborationPackageActions.onboardNonExistingClientFailed({ error }))),
          ),
      ),
    ),
  );

  openOnboardingInNewTab$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CollaborationPackageActions.onboardExistingClientSucceeded,
          CollaborationPackageActions.onboardNonExistingClientSucceeded,
        ),
        tap(({ key }) => {
          openOnboardingInNewTab(key, this.translocoSerive.getActiveLang());
        }),
      ),
    { dispatch: false },
  );

  // -- ONBOARDING WITH ANNUAL CLIENT COLLABORATION PACKAGE --

  onboardWithAnnualClientCollaborationPackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.onboardWithAnnualClientCollaborationPackage),
      switchMap(({ cloudApiKey, name, socialSecurityNumber }) =>
        this.onboardingService
          .onboardCompanyWithAnnualClientCollaborationPackage({ cloudApiKey, name, socialSecurityNumber })
          .pipe(
            mergeMap((data) => [
              CollaborationPackageActions.onboardWithAnnualClientCollaborationPackageSucceeded({ name }),
              ListActions.updateItemInListSucceeded({
                item: data,
                callback: (persistedItem, items) =>
                  // eslint-disable-next-line max-nested-callbacks
                  items.map((item) => {
                    if (item.cloudApiKey !== persistedItem.publicKey) {
                      return item;
                    }

                    // Update current item with the new subscription information
                    const { modules, integrations, subscriptionTypeId } = persistedItem;
                    return {
                      ...item,
                      subscription: { ...item.subscription, modules, integrations, subscriptionTypeId },
                      canChangeToAnnualClient: false,
                    } as CollaborationPackageItem;
                  }),
              }),
            ]),
            catchError((error: unknown) =>
              of(CollaborationPackageActions.onboardWithAnnualClientCollaborationPackageFailed({ error })),
            ),
          ),
      ),
    ),
  );

  onboardAnnualClientCollaborationPackageSucceededMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.onboardWithAnnualClientCollaborationPackageSucceeded),
      map(({ name }) =>
        ToastActions.showInfoMessage({
          summary: this.translate('annualClientPackageHeadline'),
          detail: this.translate('annualClientPackageText', { params: { name } }),
        }),
      ),
    ),
  );

  // -- CHANGE PACKAGE ON NEW SUBSCRIPTION --

  changePackageOnNewSubscription$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CollaborationPackageActions.changePackageOnNewSubscriptionType),
        tap(({ cloudApiKey }) => {
          this.openBlappInNewTab(cloudApiKey);
        }),
      ),
    { dispatch: false },
  );

  // -- ARCHIVE CLOUD DATABASE --

  archiveCloudDatabase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.archiveCloudDatabase),
      switchMap((payload) =>
        this.archiveCloudCompany(payload).pipe(
          map(() =>
            CollaborationPackageActions.archiveCloudDatabaseSucceeded({
              cloudApiKey: payload.cloudApiKey,
              name: payload.name,
              clientAction: payload.clientAction,
            }),
          ),
          catchError((error: unknown) => of(CollaborationPackageActions.archiveCloudDatabaseFailed({ error }))),
        ),
      ),
    ),
  );

  archiveCloudDatabaseSucceededMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.archiveCloudDatabaseSucceeded),
      map(({ name }) =>
        ToastActions.showInfoMessage({
          summary: this.translate('databaseIsArchived'),
          detail: this.translate('databaseIsArchivedText', { params: { name } }),
        }),
      ),
    ),
  );

  // -- UNARCHIVE/REACTIVATE CLOUD DATABASE --

  unarchiveCloudDatabase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.unarchiveCloudDatabase),
      switchMap((payload) =>
        this.unarchiveCloudCompany(payload).pipe(
          map(() =>
            CollaborationPackageActions.unarchiveCloudDatabaseSucceeded({
              cloudApiKey: payload.cloudApiKey,
              name: payload.name,
              clientAction: payload.clientAction,
            }),
          ),
          catchError((error: unknown) => of(CollaborationPackageActions.unarchiveCloudDatabaseFailed({ error }))),
        ),
      ),
    ),
  );

  unarchiveCloudDatabaseSucceededMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.unarchiveCloudDatabaseSucceeded),
      map(({ name }) =>
        ToastActions.showInfoMessage({
          summary: this.translate('databaseIsUnarchived'),
          detail: this.translate('databaseIsUnarchivedText', { params: { name } }),
        }),
      ),
    ),
  );

  reloadListWhenUnachiveIsSuccessfullyDone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.unarchiveCloudDatabaseSucceeded),
      concatLatestFrom(() =>
        this.store.select(ListSelectors.getFilters<CollaborationPackageFilter, CollaborationPackageParams>()),
      ),
      map(([, listFilter]) => CollaborationPackageActions.load(listFilter)),
    ),
  );

  // -- DELETE CLOUD DATABASE --

  deleteCloudDatabase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.deleteCloudDatabase),
      switchMap(({ cloudApiKey, name, clientAction }) =>
        this.cloudCompanyService.deleteCloudCompany(cloudApiKey).pipe(
          map(() => CollaborationPackageActions.deleteCloudDatabaseSucceeded({ cloudApiKey, name, clientAction })),
          catchError((error: unknown) => of(CollaborationPackageActions.deleteCloudDatabaseFailed({ error }))),
        ),
      ),
    ),
  );

  deleteCloudDatabaseSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CollaborationPackageActions.deleteCloudDatabaseSucceeded),
      map(({ name }) =>
        ToastActions.showInfoMessage({
          summary: this.translate('databaseIsDeleted'),
          detail: this.translate('databaseIsDeletedText', { params: { name } }),
        }),
      ),
    ),
  );

  // Removes the row from the list when archived
  removeCloudDatabaseFromListOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CollaborationPackageActions.archiveCloudDatabaseSucceeded,
        CollaborationPackageActions.deleteCloudDatabaseSucceeded,
      ),
      map(({ cloudApiKey }) =>
        ListActions.deleteItemInListSucceeded({
          item: null,
          predicate: (item: CollaborationPackageItem) => item.cloudApiKey !== cloudApiKey,
        }),
      ),
    ),
  );

  // Archives the client in Workflow
  archivedClientWhenSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CollaborationPackageActions.archiveCloudDatabaseSucceeded,
        CollaborationPackageActions.deleteCloudDatabaseSucceeded,
      ),
      filter(({ clientAction }) => clientAction === ClientActionType.ARCHIVE),
      concatLatestFrom(() => this.store.select(CollaborationPackageSelectors.selectClient)),
      filter(([, client]) => Boolean(client)),
      map(([, client]) =>
        ClientActions.archiveDeletableClient({
          client: { ...client, archived: true },
          predicate: null,
        }),
      ),
    ),
  );

  // Delete the client in Workflow
  deleteClientWhenSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CollaborationPackageActions.archiveCloudDatabaseSucceeded,
        CollaborationPackageActions.deleteCloudDatabaseSucceeded,
      ),
      filter(({ clientAction }) => clientAction === ClientActionType.DELETE),
      concatLatestFrom(() => this.store.select(CollaborationPackageSelectors.selectClient)),
      filter(([, client]) => Boolean(client?.deletable)),
      map(([, client]) =>
        ClientActions.deleteClient({
          client,
          predicate: null,
        }),
      ),
    ),
  );

  showDialogIfMissingCollaborationPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.afterLoginSuccessful),
      filter(() => this.allInclusiveService.isLoggedInWithBLTAService),
      switchMap(() =>
        this.cloudCompanyService.getCollaborationCloudCompanies({ responsibleId: null }).pipe(
          tap(({ meta }) => this.setCollaborationPackageMeta(meta)),
          catchError(() => of({ meta: { numberOfMissingCollaborationPackages: 0 } })),
        ),
      ),
      map(({ meta }: CollaborationPackageResponse) => meta.numberOfMissingCollaborationPackages),
      filter((numberOfMissingCollaborationPackages) => numberOfMissingCollaborationPackages > 0),
      concatLatestFrom(() => this.store.select(FirmSelectors.selectCountryCode)),
      tap(([numberOfMissingCollaborationPackages, firmCountryCode]) =>
        openTranslatableDialog({
          deps: { dialogService: this.dialogService, translationService: this.translocoSerive, store: this.store },
          componentType: MissingCollaborationPackagesInfoDialogComponent,
          data: {
            numberOfMissingCollaborationPackages,
            actingOnMarket: firmCountryCode,
          },
        }),
      ),
      map(() => CollaborationPackageActions.showingMissingCollaborationPackagesDialog({ visible: true })),
    ),
  );

  // eslint-disable-next-line no-useless-constructor, @typescript-eslint/no-useless-constructor, max-params
  constructor(
    private cloudCompanyService: CloudCompanyService,
    private onboardingService: OnboardingService,
    private clientService: ClientService,
    private allInclusiveService: AllInclusiveService,
    private dialogService: DialogService,
    private translocoSerive: TranslocoService,
  ) {
    super('CollaborationPackageEffects');
  }

  private openBlappInNewTab(cloudApiKey: string): void {
    window.open(`${environment.blappWeb}/${cloudApiKey}?lang=${this.translocoSerive.getActiveLang()}`, '_blank');
  }

  private getClientById(clientId: number) {
    return this.clientService.getClientById(clientId).pipe(map((client) => toClientTypeTransformer.transform(client)));
  }

  private archiveCloudCompany(
    payload: ReturnType<typeof CollaborationPackageActions.archiveCloudDatabase>,
  ): Observable<void> {
    const { cloudApiKey, years } = payload;
    return this.cloudCompanyService.archiveCloudCompany(cloudApiKey, years);
  }

  private unarchiveCloudCompany(
    payload: ReturnType<typeof CollaborationPackageActions.unarchiveCloudDatabase>,
  ): Observable<void> {
    const { cloudApiKey, name, socialSecurityNumber } = payload;
    return this.cloudCompanyService.unarchiveCloudCompany(cloudApiKey, { clientName: name, socialSecurityNumber });
  }

  private setCollaborationPackageMeta(meta: CollaborationPackageResponse['meta']): void {
    this.store.dispatch(CollaborationPackageActions.setMeta({ meta }));
  }
}
